import { PAYMENT_MODULE } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useMemo, useState } from "react";
import { useAPIUtils } from "../../useAPIUtils";
import { IPaymentModeListResponseData } from "@/api_framework/api_modals/payment";
import { isPosCashAllowedRole, isPosCashMode } from "@/constants";

AxiosCurlirize(axios, () => {});
type ICartAPIResponse = { status: boolean; message: string };

export const usePaymentMethodListAPI = () => {
  const configuration = useConfiguration();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const roleName = useAppSelector((state) => state?.auth?.userPermissions?.role_name);
  const canUsePosCash = isPosCashAllowedRole(roleName);
  const [paymentMode, setPaymentMode] = useState<IPaymentModeListResponseData[]>([]);
  const [onlinePaymentMode, setOnlinePaymentMode] = useState<IPaymentModeListResponseData[]>([]);
  const [paymentMachine, setPaymentMachine] = useState<IPaymentModeListResponseData[]>([]);
  const dispatch = useAppDispatch();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();

  const execute = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const response: ICartAPIResponse = await axios
        .get(`${PAYMENT_MODULE?.PAYMENT_MODE_LIST?.baseURL}?clinic_id=${mappedClinicId}`, {
          headers: {
            Authorization: `${accessToken}`,
            "Content-Type": "application/json",
          },
        })
        .then((res: AxiosResponse<any>) => {
          if (res.data.status === true) {
            setPaymentMode(res.data.data?.offline_payment_modes ?? []);
            setOnlinePaymentMode(res.data.data?.online_payment_modes ?? []);
            setPaymentMachine(res.data.data?.clinic_pos_mapping);

            return { status: true, message: "Cart Level Coupons Fetched" };
          } else {
            setPaymentMode([]);
            setOnlinePaymentMode([]);
            setPaymentMachine([]);

            return { status: false, message: "No Cart Level Coupons" };
          }
        })
        .catch((e: AxiosError) => {
          setPaymentMode([]);
          setOnlinePaymentMode([]);
          setPaymentMachine([]);
          sendCurl(e.config["curlCommand"]);
          captureError(e);

          return { status: false, message: "cart_not_filtered" };
        });
      return response;
    } catch (e: any) {
      setPaymentMode([]);

      captureError(e);
    }
  }, [dispatch, mappedClinicId]);

  // POS-CASH is returned for every clinic; only the allowlisted roles may see it.
  // Filtered on read, not in the response handler: userPermissions is fetched
  // asynchronously and is still null when this API resolves right after a page
  // refresh, so a role read at response time would hide POS-CASH from an
  // allowlisted user for the whole session.
  //
  // Both lists are filtered: the mode is expected in online_payment_modes, but a
  // copy in offline_payment_modes would otherwise hand every role a way around
  // the restriction.
  const withoutPosCash = useCallback(
    (modes: IPaymentModeListResponseData[]) =>
      canUsePosCash ? modes : (modes ?? []).filter((m) => !isPosCashMode(m?.payment_mode)),
    [canUsePosCash],
  );

  const visiblePaymentMode = useMemo(() => withoutPosCash(paymentMode), [paymentMode, withoutPosCash]);
  const visibleOnlinePaymentMode = useMemo(() => withoutPosCash(onlinePaymentMode), [onlinePaymentMode, withoutPosCash]);

  return {
    paymentMode: visiblePaymentMode,
    execute,
    onlinePaymentMode: visibleOnlinePaymentMode,
    paymentMachine,
  };
};
